// Phase 1A Phase 9B.4C — Home Activity Snapshot pure helper.
//
// Pure adapter from existing read-only logged event sources into a
// compact deterministic view-model for the /home Activity Snapshot
// widget. NO React, NO Supabase, NO browser APIs, NO clock reads, NO
// side effects, NO mutation. Returns fresh arrays each call.
//
// Truth contract:
//   - Every category title and body is past-tense and deterministic.
//     The widget describes records that already exist; it never
//     announces real-time events, automation, or notifications.
//   - mergeActivityItems sorts by timestamp descending; null
//     timestamps are placed last so undated rows don't displace dated
//     ones. Truncation respects the merged order.
//   - Forbidden positive vocabulary (live monitoring / agent is
//     watching / automation triggered / notification sent / cashout
//     executed / won / lost …) is excluded by construction; the
//     test file forbids reintroduction.
//   - hrefs are added only where a real destination exists. /vault is
//     used for cycle-anchored items; audit events have no href because
//     /vault is per-chain, not per-event.

import type { Database } from "../../db/types.ts";

export type HomeActivityCategory = "agent_log" | "audit_event" | "cashout" | "compounding";

export interface HomeActivityItem {
  id: string;
  timestamp: string | null;
  category: HomeActivityCategory;
  title: string;
  body: string;
  href?: string;
}

// ─── Source row types (subset of Database, only fields we read) ───

export type AgentLogRow = Pick<
  Database["public"]["Views"]["agent_logs_for_user"]["Row"],
  "id" | "agent_name" | "created_at" | "cycle_id"
>;

export type RgAuditRow = Pick<
  Database["public"]["Tables"]["rg_audit_events"]["Row"],
  "id" | "created_at" | "event_type"
>;

export type CashoutRow = Pick<
  Database["public"]["Tables"]["cashout_events"]["Row"],
  "id" | "created_at" | "cycle_id" | "cashout_value"
>;

export type DecisionRow = Pick<
  Database["public"]["Tables"]["rollover_compounding_decisions"]["Row"],
  "id" | "created_at" | "cycle_id"
>;

// ─── Constants ──────────────────────────────────────────────────

export const HOME_ACTIVITY_FOOTNOTE =
  "Phase-1A deterministic. Read-only. No realtime monitoring, no live alerts, no automation.";

export const HOME_ACTIVITY_DEFAULT_MAX = 5;

const TITLE_AGENT_LOG = "Reasoning log written";
const TITLE_AUDIT_EVENT = "Audit event logged";
const TITLE_CASHOUT = "Cashout decision recorded";
const TITLE_COMPOUNDING = "Compounding decision recorded";

const BODY_AGENT_LOG = "Phase-1A deterministic agent reasoning recorded for this cycle.";
const BODY_CASHOUT = "Phantom-simulated cashout decision logged. No real-money settlement.";
const BODY_COMPOUNDING = "Post-win compounding decision logged.";

// ─── Adapters ───────────────────────────────────────────────────

export function agentLogsToItems(rows: readonly AgentLogRow[]): HomeActivityItem[] {
  const out: HomeActivityItem[] = [];
  for (const r of rows) {
    if (r.id === null) continue;
    const item: HomeActivityItem = {
      id: r.id,
      timestamp: r.created_at,
      category: "agent_log",
      title: TITLE_AGENT_LOG,
      body: BODY_AGENT_LOG,
    };
    if (typeof r.cycle_id === "string" && r.cycle_id.length > 0) {
      item.href = "/vault";
    }
    out.push(item);
  }
  return out;
}

export function auditEventsToItems(rows: readonly RgAuditRow[]): HomeActivityItem[] {
  const out: HomeActivityItem[] = [];
  for (const r of rows) {
    out.push({
      id: r.id,
      timestamp: r.created_at,
      category: "audit_event",
      title: TITLE_AUDIT_EVENT,
      body: `System record · ${r.event_type}`,
    });
  }
  return out;
}

export function cashoutEventsToItems(rows: readonly CashoutRow[]): HomeActivityItem[] {
  const out: HomeActivityItem[] = [];
  for (const r of rows) {
    const item: HomeActivityItem = {
      id: r.id,
      timestamp: r.created_at,
      category: "cashout",
      title: TITLE_CASHOUT,
      body: BODY_CASHOUT,
    };
    if (typeof r.cycle_id === "string" && r.cycle_id.length > 0) {
      item.href = "/vault";
    }
    out.push(item);
  }
  return out;
}

export function compoundingDecisionsToItems(rows: readonly DecisionRow[]): HomeActivityItem[] {
  const out: HomeActivityItem[] = [];
  for (const r of rows) {
    const item: HomeActivityItem = {
      id: r.id,
      timestamp: r.created_at,
      category: "compounding",
      title: TITLE_COMPOUNDING,
      body: BODY_COMPOUNDING,
    };
    if (typeof r.cycle_id === "string" && r.cycle_id.length > 0) {
      item.href = "/vault";
    }
    out.push(item);
  }
  return out;
}

// ─── Merge / sort / truncate ────────────────────────────────────

export function mergeActivityItems(
  lists: readonly HomeActivityItem[][],
  maxItems: number = HOME_ACTIVITY_DEFAULT_MAX,
): HomeActivityItem[] {
  const merged: HomeActivityItem[] = [];
  for (const list of lists) {
    for (const item of list) merged.push(item);
  }
  // Sort by timestamp DESC; null timestamps last. Stable on ties via id.
  merged.sort((a, b) => {
    const aNull = a.timestamp === null;
    const bNull = b.timestamp === null;
    if (aNull && bNull) return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    if (aNull) return 1;
    if (bNull) return -1;
    if (a.timestamp! < b.timestamp!) return 1;
    if (a.timestamp! > b.timestamp!) return -1;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
  if (maxItems < 0) return [];
  return merged.slice(0, maxItems);
}
